<template>
  <div class="unused-term container" v-loading.fullscreen.lock="fullLoading">
    <el-tooltip
      placement="top"
      content="请提交所有错误句子后再点击下一步哦！"
      :disabled="canSubmit"
    >
      <el-button
        class="next-btn"
        type="primary"
        @click="canSubmit && checkAndNext()"
        :class="{ disabled: loading || !canSubmit }"
      >
        {{ hasNext ? '下一步' : '提交' }}
      </el-button>
    </el-tooltip>
    <p class="title d-f align-c operate-bar">
      <span>术语缺失：</span>
      <span>{{ errorCount ?? 0 }}句</span>

      <el-button
        class="batch-submit"
        type="primary"
        @click="batchSubmit"
        :disabled="!multiSelection?.length"
      >
        批量提交
      </el-button>
    </p>
    <div class="table-wrap">
      <el-table
        border
        :data="records"
        v-loading="loading"
        :row-class-name="
          ({ row }) => row.qcStatus === submitStatusEnum.done && 'submit'
        "
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="srcText" label="原文">
          <template slot-scope="scope" v-if="scope.row && !loading">
            <rich-text
              :pId="scope.row.id"
              :jsonText="scope.row.srcText"
              readonly
            />
          </template>
        </el-table-column>
        <el-table-column
          prop="qcText"
          label="译文"
          class-name="rich-text-column"
        >
          <template slot-scope="scope" v-if="scope.row && !loading">
            <rich-text
              :pId="scope.row.id"
              :key="`${scope.row.id}-${scope.row.updateSign}`"
              :hasToolbar="scope.row.useRichText"
              :class="{
                'edit-un-submit': scope.row.editedText !== scope.row.oriText,
              }"
              :jsonText="scope.row.qcText"
              :errors="scope.row.errors"
              @change="(text, json) => handleChange(text, json, scope.row)"
              @focus="handleFocus"
              @blur="(text, json) => handleBlur(text, json, scope.row)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="termInfos" label="错误类型">
          <template slot-scope="scope">
            <div v-if="scope.row.termInfos && scope.row.termInfos.length">
              <p>未使用术语</p>
              <div v-for="(item, index) in scope.row.termInfos" :key="index">
                <p>{{ index + 1 }}. {{ item.srcText }}</p>
                <p>{{ item.tranText }}</p>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="150" align="center" label="操作">
          <template slot-scope="scope">
            <el-button type="primary" @click="handleSubmit(scope.row)">
              {{ scope.row.qcStatus === submitStatusEnum.done ? '已提交' : '提交' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        background
        layout="prev, pager, next"
        :current-page.sync="page.current"
        :total="page.total"
        :page-size="page.size"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import {
  getUnusedTerm,
  modifyUnusedTerm,
  batchModifyUnusedTerm,
} from '@/api/qc/check';
import RichText, { jsonTextToText } from '@/components/rich-text';
import { rebuildEditorErrors } from '@/util/dot';
import { hasValidChar } from '@/util/string';
import loadAndCheckMixin from './loadAndCheckMixin.js';
import { STEP } from './const';

export default {
  name: 'unused-term',
  // ⚠️：部分公共逻辑在mixin中！
  mixins: [loadAndCheckMixin],
  components: {
    RichText,
  },
  computed: {
    hasNext() {
      return this.flowCount > 3;
    },
  },
  methods: {
    checkAndNext() {
      if (this.loading) return;
      const errorCount = Number(this.errorCount);

      // 没有错误，直接 next 或者 submit
      if (errorCount === 0) {
        if (this.hasNext) {
          this.next(STEP.two);
        } else {
          this.$confirm('确认要提交吗', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          })
            .then(() => {
              // next转提交会在外层主流程控制
              this.next(STEP.two);
            })
            .catch(() => {});
        }
        return;
      }

      this.$confirm(
        `检测到存在术语缺失，直接提交有较大风险，点击下一步无法返回，确认提交吗？`,
        '提示',
        {
          confirmButtonText: '坚持提交',
          cancelButtonText: '返回修改',
          closeOnClickModal: false,
          type: 'warning',
        },
      )
        .then(() => {
          this.next(STEP.two);
        })
        .catch(() => {});
    },
    fetchListData() {
      this.loading = true;
      getUnusedTerm({
        current: this.page.current,
        size: this.page.size,
        orderId: this.queryParams.id,
      }).then((res) => {
        this.loading = false;
        const usableData = res.data?.data ?? {};
        this.records = usableData.records ?? [];
        this.errorCount = usableData.maxLimit;
        this.page.total = usableData.total;
        // 是否可以提交
        this.canSubmit = usableData.hitCount;
        // 自定义的字段需要额外添加，防止丢失响应式
        this.records = this.records.map((item) => ({
          ...item,
          editedText: item.qcText && jsonTextToText(item.qcText),
          // qcText 是jsonStr类型，这里需要转成text类型，用于比对
          oriText: item.qcText && jsonTextToText(item.qcText),
          errors: rebuildEditorErrors(item.errDtoList),
          editedJson: item.qcText,
          updateSign: 0,
        }));
      });
    },
    handleSubmit(row) {
      const isDigitMatched = this.sentDigitCheck(row);
      if (!isDigitMatched) return;

      if (!hasValidChar(row.editedText)) {
        this.$confirm(
          '<h4>确定要将此句译文保存为空吗?</h4><div>请谨慎操作！译文为空的场景一般应用于原文断句异常时，已将此句的译文合并入其他句子中。</div>',
          '译文为空预警',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          },
        )
          .then(() => {
            this.doSubmit(row);
          })
          .catch(() => {
            row.editedText = row.qcText && jsonTextToText(row.qcText);
            row.editedJson = row.qcText;
            row.errors = rebuildEditorErrors(row.errDtoList);
            row.updateSign++;
          });
      } else {
        this.doSubmit(row);
      }
    },
    batchSubmit() {
      if (!this.multiSelection?.length) return;
      this.batchSubmitCheck()
        .then(() => {
          this.fullLoading = true;
          batchModifyUnusedTerm(
            this.multiSelection.map((item) => ({
              id: item.id,
              orderId: this.queryParams.id,
              slan: this.queryParams.slan,
              tlan: this.queryParams.tlan,
              qcTxt: item.editedJson,
            })),
          )
            .then(() => {
              this.$message({
                message: '提交成功',
                type: 'success',
              });
              this.fetchListData();
            })
            .catch((err) => {
              if (err.message === '该质控任务状态已修改') {
                this.$closeAndGoBack();
              }
            })
            .finally(() => {
              this.fullLoading = false;
            });
        })
        .catch(() => {});
    },
    doSubmit(row) {
      modifyUnusedTerm({
        id: row.id,
        orderId: this.queryParams.id,
        qcTxt: row.editedJson,
        slan: this.queryParams.slan,
        tlan: this.queryParams.tlan,
      })
        .then(() => {
          this.$message({
            message: '提交成功',
            type: 'success',
          });
          this.fetchListData();
        })
        .catch((err) => {
          if (err.message === '该质控任务状态已修改') {
            this.$closeAndGoBack();
          }
        });
    },
  },
};
</script>
